import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import slaService from "@/services/sla";
import { SLA, SLAFormData, SLAPriority, SLAType } from "@/types/sla";
import { useState } from "react";

interface SLAFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  initialData?: SLA;
  mode: "create" | "edit";
  projectId: string;
}

export default function SLAForm({
  open,
  onClose,
  onSuccess,
  initialData,
  mode,
  projectId,
}: SLAFormProps) {
  const { toast } = useToast();
  const [formData, setFormData] = useState<SLAFormData>(
    initialData
      ? {
        name: initialData.name,
        type: initialData.type,
        priority: initialData.priority,
        timeInMinutes: initialData.timeInMinutes,
        isActive: initialData.isActive,
      }
      : {
        name: "",
        type: "response_time",
        priority: "medium",
        timeInMinutes: 60,
        isActive: true,
      }
  );
  const [loading, setLoading] = useState(false);

  const handleChange = (
    field: string,
    value: string | number | boolean
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast({
        title: "Erro",
        description: "Nome do SLA é obrigatório",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      if (mode === "create") {
        const [response, error] = await slaService.create(projectId, formData);

        if (error) {
          toast({
            title: "Erro",
            description: error.msg || "Erro ao criar SLA",
            variant: "destructive",
          });
          return;
        }

        toast({
          title: "Sucesso",
          description: "SLA criado com sucesso",
        });
      } else if (mode === "edit" && initialData) {
        const [response, error] = await slaService.update(
          projectId,
          initialData._id,
          formData
        );

        if (error) {
          toast({
            title: "Erro",
            description: error.msg || "Erro ao atualizar SLA",
            variant: "destructive",
          });
          return;
        }

        toast({
          title: "Sucesso",
          description: "SLA atualizado com sucesso",
        });
      }

      onSuccess();
      onClose();
    } catch (error) {
      toast({
        title: "Erro",
        description: "Ocorreu um erro ao processar a requisição",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {mode === "create" ? "Novo SLA" : "Editar SLA"}
            </DialogTitle>
            <DialogDescription>
              {mode === "create"
                ? "Preencha os dados para criar um novo SLA"
                : "Atualize os dados do SLA"}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="name" className="text-right">
                Nome
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                className="col-span-3"
                placeholder="Nome do SLA"
              />
            </div>

            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="type" className="text-right">
                Tipo
              </Label>
              <Select
                value={formData.type}
                onValueChange={(value: SLAType) => handleChange("type", value)}
              >
                <SelectTrigger id="type" className="col-span-3">
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="response_time">Tempo de Resposta</SelectItem>
                  <SelectItem value="resolution_time">Tempo de Resolução</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="priority" className="text-right">
                Prioridade
              </Label>
              <Select
                value={formData.priority}
                onValueChange={(value: SLAPriority) => handleChange("priority", value)}
              >
                <SelectTrigger id="priority" className="col-span-3">
                  <SelectValue placeholder="Selecione a prioridade" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Baixa</SelectItem>
                  <SelectItem value="medium">Média</SelectItem>
                  <SelectItem value="high">Alta</SelectItem>
                  <SelectItem value="critical">Crítica</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="timeInMinutes" className="text-right">
                Tempo (minutos)
              </Label>
              <Input
                id="timeInMinutes"
                type="number"
                min="1"
                value={formData.timeInMinutes}
                onChange={(e) => handleChange("timeInMinutes", parseInt(e.target.value))}
                className="col-span-3"
              />
            </div>

            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="isActive" className="text-right">
                Ativo
              </Label>
              <div className="col-span-3 flex items-center space-x-2">
                <Switch
                  id="isActive"
                  checked={formData.isActive}
                  onCheckedChange={(checked) => handleChange("isActive", checked)}
                />
                <span>{formData.isActive ? "Sim" : "Não"}</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" type="button" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={loading}>
              {loading
                ? "Salvando..."
                : mode === "create"
                  ? "Criar"
                  : "Salvar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
